import React, { useState, useMemo, useEffect } from "react";
import { SectionCard } from "../../../../Component/AgentComponents/SectionCard";
import RetailerDetailPanel from "./RetailerDetailPanel";
import { useNavigate } from "react-router-dom";
import RefreshButton from "../../../../Component/Common/RefreshButton";
import { Eye } from "lucide-react";
import SimpleTable from "../../../../Component/Common/SimpleTable";
import Badge from "../../../../Component/Common/Badge";
import RegularLoader from "../../../../Component/Loader/RegularLoader";
import Pagination from "../../../../Component/Common/Pagination";
import { BASE_URL } from "../../../../config/api";
import axios from "axios";

/* ================= MOBILE CARD ================= */

function RetailerMobileCard({ retailer, onSelect, onView }) {
  return (
    <div
      onClick={() => onSelect(retailer)}
      className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2 active:bg-slate-50"
    >
      {/* HEADER */}
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            {retailer.business_name || retailer.contact_person || "—"}
          </p>
          <p className="text-[11px] text-slate-500">
            {retailer.city || "—"} · RT-{retailer.id}
          </p>
        </div>

        <Badge
          label={retailer.status === 1 ? "Active" : "Inactive"}
          color={retailer.status === 1 ? "green" : "red"}
        />
      </div>

      {/* META */}
      <p className="text-[11px] text-slate-500">
        Onboarded on{" "}
        <span className="font-medium">
          {retailer.created_at?.split("T")[0] || "—"}
        </span>
      </p>

      {/* ACTION */}
      <div className="flex justify-end pt-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onView(retailer);
          }}
          className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
        >
          <Eye className="h-4 w-4" />
          View
        </button>
      </div>
    </div>
  );
}

/* ================= MAIN VIEW ================= */

export default function RetailersView() {
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchRetailer, setSearchRetailer] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRetailer, setSelectedRetailer] = useState(null);

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
  const agentId = authUser?.id;

  const navigate = useNavigate();

  /* ================= FETCH ================= */

  const fetchRetailers = async () => {

    try {

      setLoading(true);

      const response =
        await axios.get(
          `${BASE_URL}common/get-users`,
          {
            params: {
              role: 5,
              page: currentPage,
              agent_id: agentId,
            },

            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const json =
        response.data;

      setRetailers(
        json?.data || []
      );

      setTotalPages(
        json?.pagination
          ?.last_page || 1
      );

    } catch (err) {

      console.error(
        "Failed to fetch retailers",
        err
      );

    } finally {

      setLoading(false);

    }
  };

  useEffect(() => {
    fetchRetailers();
  }, [currentPage]);

  useEffect(() => {
    if (retailers.length && !selectedRetailer) {
      setSelectedRetailer(retailers[0]);
    }
  }, [retailers]);

  /* ================= FILTER ================= */

  const filteredRetailers = useMemo(() => {
    const q = searchRetailer.toLowerCase();
    return retailers.filter(
      (r) =>
        r.business_name?.toLowerCase().includes(q) ||
        r.contact_person?.toLowerCase().includes(q) ||
        r.city?.toLowerCase().includes(q) ||
        String(r.id).includes(q)
    );
  }, [retailers, searchRetailer]);

  /* ================= TABLE ROWS ================= */

  const tableRows = useMemo(() => {
    return filteredRetailers.map((r) => ({
      raw: r,
      cells: [
        {
          title: r.business_name || r.contact_person || "—",
          sub: `RT-${r.id}`,
          extra: `Onboard: ${r.created_at?.split("T")[0] || "—"}`,
        },
        r.city || "—",
        (
          <Badge
            label={r.status === 1 ? "Active" : "Inactive"}
            color={r.status === 1 ? "green" : "red"}
          />
        ),
      ],
    }));
  }, [filteredRetailers]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* ================= LIST ================= */}
      <div className="lg:col-span-2 space-y-3">
        <SectionCard
          title="ARP & Earnings"
          right={
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search by name, code, city..."
                value={searchRetailer}
                onChange={(e) => setSearchRetailer(e.target.value)}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1 w-48 md:w-64"
                disabled={loading}
              />
              <RefreshButton onClick={fetchRetailers} disabled={loading} />
            </div>
          }
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RegularLoader />
            </div>
          ) : filteredRetailers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500 text-sm">
              <p className="font-medium">No ARP found</p>
              <p className="text-xs mt-1">
                ARPs added under you will appear here
              </p>
            </div>
          ) : (
            <>
              {/* ========== MOBILE CARDS ========== */}
              <div className="space-y-3 lg:hidden">
                {filteredRetailers.map((r) => (
                  <RetailerMobileCard
                    key={r.id}
                    retailer={r}
                    onSelect={setSelectedRetailer}
                    onView={(row) =>
                      navigate(`/user/${row.id}`, {
                        state: { entityType: "retailer", data: row },
                      })
                    }
                  />
                ))}
              </div>

              {/* ========== DESKTOP TABLE ========== */}
              <div className="hidden lg:block">
                <SimpleTable
                  headers={["Retailer", "City", "Status"]}
                  rows={tableRows}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  showSrNo
                  onRowClick={(row) => setSelectedRetailer(row)}
                  renderActions={(row) => (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/user/${row.id}`, {
                          state: { entityType: "retailer", data: row },
                        });
                      }}
                      className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </button>
                  )}
                />
              </div>

              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </SectionCard>
      </div>

      {/* ================= DETAIL PANEL ================= */}
      <RetailerDetailPanel retailer={selectedRetailer} />
    </div>
  );
}


// import React, { useState, useMemo, useEffect } from "react";
// import { SectionCard } from "../../../../Component/AgentComponents/SectionCard";

// import RetailerDetailPanel from "./RetailerDetailPanel";
// import { useNavigate } from "react-router-dom";

// import RefreshButton from "../../../../Component/Common/RefreshButton";
// import { Eye } from "lucide-react";
// import SimpleTable from "../../../../Component/Common/SimpleTable";
// import Badge from "../../../../Component/Common/Badge";
// import RegularLoader from "../../../../Component/Loader/RegularLoader";
// import { BASE_URL } from "../../../../config/api";



// export default function RetailersView() {
//   const [retailers, setRetailers] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [searchRetailer, setSearchRetailer] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const [selectedRetailer, setSelectedRetailer] = useState(null);

//    const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;
//   const id = authUser.id;

//   const navigate = useNavigate();

//   // Pagination calculation


 
//   const fetchRetailers = async () => {
//   try {
//     setLoading(true);

//     const res = await fetch(
//       `${BASE_URL}common/get-users?role=5&page=${currentPage}&agent_id=${id}`  
//     );

//     const json = await res.json();

//     setRetailers(json?.data || []);
//     setTotalPages(json?.pagination?.last_page || 1);

//   } catch (err) {
//     console.error("Failed to fetch retailers", err);
//   } finally {
//     setLoading(false);
//   }
// };

// useEffect(() => {
//   fetchRetailers();
// }, [currentPage]);

// const tableRows = useMemo(() => {
//   return retailers.map((r) => ({
//     raw: r,
//     cells: [
//       {
//         title: r.business_name || r.contact_person || "—",
//         sub: `RT-${r.id}`,
//         extra: `Onboard: ${r.created_at?.split("T")[0] || "—"}`
//       },
//       r.city || "—",
//       <>
//         <Badge
//           label={r.status === 1 ? "Active" : "Inactive"}
//           color={r.status === 1 ? "green" : "red"}
//         />
//         {/* <Badge
//           label={r.is_payment_success === 1 ? "Selling" : "Not selling"}
//           color={r.is_payment_success === 1 ? "blue" : "gray"}
//         /> */}
//       </>

//     ]
//   }));
// }, [retailers]);


// useEffect(() => {
//   setCurrentPage(1);
// }, [searchRetailer]);

// useEffect(() => {
//   if (retailers.length && !selectedRetailer) {
//     setSelectedRetailer(retailers[0]);
//   }
// }, [retailers]);
//   return (
//     <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
//       {/* List */}
//       <div className="lg:col-span-2 space-y-3">
//         <SectionCard
//           title="Retailers & Earnings"
//           right={
//             <div className="flex gap-2">
//               <input
//                 type="text"
//                 placeholder="Search by name, code, city..."
//                 value={searchRetailer}
//                 onChange={(e) => setSearchRetailer(e.target.value)}
//                 className="text-xs border border-slate-200 rounded-lg px-2 py-1 w-48 md:w-64"
//                 disabled={loading}
//               />
//               <RefreshButton onClick={fetchRetailers} disabled={loading} />
//             </div>
//           }
//         >
//           {loading ? (
//   <div className="flex items-center justify-center py-12">
//     <RegularLoader />
//   </div>
//             ) : retailers.length === 0 ? (
//               <div className="flex flex-col items-center justify-center py-16 text-slate-500 text-sm">
//                 <p className="font-medium">No retailers found</p>
//                 <p className="text-xs mt-1">Retailers added under you will appear here</p>
//               </div>
//             ) : (
//               <SimpleTable
//                 headers={["Retailer", "City", "Status"]}
//                 rows={tableRows}
//                 currentPage={currentPage}
//                 totalPages={totalPages}
//                 onPageChange={setCurrentPage}
//                 showSrNo
//                 onRowClick={(row) => setSelectedRetailer(row)}
//                 renderActions={(row) => (
//                   <button
//                     type="button"
//                     onClick={(e) => {
//                       e.stopPropagation();
//                       navigate(`/user/${row.id}`, {
//                         state: { entityType: "retailer", data: row }
//                       });
//                     }}
//                     className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
//                   >
//                     <Eye className="h-4 w-4" />
//                     View
//                   </button>
//                 )}
//               />
//             )}

//         </SectionCard>


//       </div>

//       {/* Detail Panel */}
//       <RetailerDetailPanel
//         retailer={selectedRetailer}
//         allRetailers={retailers}
//       />

//     </div>
//   );
// }


// import React from "react";
// import { SectionCard } from "../../../../Component/AgentComponents/SectionCard";
// import { Badge } from "../../../../Component/AgentComponents/Badge";
// import RetailerDetailPanel from "./RetailerDetailPanel";



// export default function RetailersView({
//   retailers,
//   allRetailers,
//   searchRetailer,
//   setSearchRetailer,
//   selectedRetailer,
//   setSelectedRetailerId,
// }) {
//   const safeRetailers = retailers || [];
//   const safeAllRetailers = allRetailers || [];

//   return (
//     <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
//       {/* List */}
//       <div className="lg:col-span-2 space-y-3">
//         <SectionCard
//           title="Retailers & Earnings"
//           right={
//             <input
//               type="text"
//               placeholder="Search by name, code, city..."
//               value={searchRetailer}
//               onChange={(e) => setSearchRetailer(e.target.value)}
//               className="text-xs border border-slate-200 rounded-lg px-2 py-1 w-48 md:w-64"
//             />
//           }
//         >
//           <div className="overflow-x-auto">
//             <table className="min-w-full text-xs">
//               <thead>
//                 <tr className="border-b border-slate-100 text-slate-500">
//                   <th className="py-2 text-left">Retailer</th>
//                   <th className="py-2 text-left">City</th>
//                   <th className="py-2 text-left">Status</th>
//                   <th className="py-2 text-right">Month Sales</th>
//                   <th className="py-2 text-right">Month Comm.</th>
//                   <th className="py-2 text-right">Lifetime Comm.</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {safeRetailers.map((r) => (
//                   <tr
//                     key={r.id}
//                     onClick={() => setSelectedRetailerId(r.id)}
//                     className={`border-b border-slate-50 cursor-pointer hover:bg-blue-50/60 ${
//                       selectedRetailer && selectedRetailer.id === r.id
//                         ? "bg-blue-50"
//                         : "bg-white"
//                     }`}
//                   >
//                     <td className="py-2">
//                       <div className="font-medium text-slate-800">
//                         {r.name}
//                       </div>
//                       <div className="text-[10px] text-slate-500">
//                         {r.code} · Onboard: {r.onboardDate}
//                       </div>
//                     </td>
//                     <td className="py-2 text-xs text-slate-600">{r.city}</td>
//                     <td className="py-2">
//                       <div className="flex flex-col gap-1">
//                         <Badge color={r.isActive ? "green" : "red"}>
//                           {r.isActive ? "Active" : "Inactive"}
//                         </Badge>
//                         <Badge color={r.isSelling ? "blue" : "gray"}>
//                           {r.isSelling ? "Selling" : "Not selling"}
//                         </Badge>
//                       </div>
//                     </td>
//                     <td className="py-2 text-xs text-right text-slate-700">
//                       ₹{r.monthlySales.toLocaleString()}
//                     </td>
//                     <td className="py-2 text-xs text-right text-blue-700">
//                       ₹{r.monthCommission.toLocaleString()}
//                     </td>
//                     <td className="py-2 text-xs text-right text-slate-700">
//                       ₹{r.totalCommission.toLocaleString()}
//                     </td>
//                   </tr>
//                 ))}
//                 {safeRetailers.length === 0 && (
//                   <tr>
//                     <td
//                       colSpan={6}
//                       className="py-4 text-center text-xs text-slate-500"
//                     >
//                       No retailers found for this search.
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </SectionCard>
//       </div>

//       {/* Detail Panel */}
//       <RetailerDetailPanel
//         retailer={selectedRetailer}
//         allRetailers={safeAllRetailers}
//       />
//     </div>
//   );
// }